import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import Tree, { TreeNode } from 'rc-tree';
import CustomModal from '../../../Modal';
import ReportComponent from '../../../../utils/ReportComponent/ReportComponent';
import CustomTooltip from '../../../Tooltip';
import Icon from '../../../Icon';
import { useTemplateConfig } from '../../../../context/TemplateConfigContext';

const reportUtils = ReportComponent.utils;

const StatisticsMetrics = ({
                                    data: propData,
                                    checkedKeys: propCheckedKeys,
                                    metrics: propMetrics,
                                    onAction: propOnAction,
                                    saveServerStorage: propSaveServerStorage,
                                    t
                                }) => {
    const { assets } = useTemplateConfig();
    const initialCheckedKeysRef = useRef(propCheckedKeys || []);
    const initialMetricsRef = useRef(propMetrics || '');

    const [state, setState] = useState({
        data: propData,
        expandedKeys: propCheckedKeys,
        checkedKeys: propCheckedKeys,
        metrics: propMetrics,
        isVisibleModal: false,
        isVisibleModalTagSettings: false
    });

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            data: propData
        }));
    }, [propData]);

    useEffect(() => {
        if (state.isVisibleModal) {
            setState(prevState => ({
                ...prevState,
                checkedKeys: propCheckedKeys,
                metrics: propMetrics
            }));
        } else {
            initialCheckedKeysRef.current = propCheckedKeys || [];
            initialMetricsRef.current = propMetrics || '';
        }
    }, [propCheckedKeys, propMetrics, state.isVisibleModal]);

    const onToggleModal = useCallback(() => {
        setState(prevState => {
            const nextVisible = !prevState.isVisibleModal;
            if (nextVisible) {
                const nextChecked = propCheckedKeys || prevState.checkedKeys || [];
                const nextMetrics = propMetrics || prevState.metrics || '';
                initialCheckedKeysRef.current = nextChecked;
                initialMetricsRef.current = nextMetrics;
                return {
                    ...prevState,
                    isVisibleModal: true,
                    checkedKeys: nextChecked,
                    expandedKeys: propCheckedKeys || prevState.expandedKeys || [],
                    metrics: nextMetrics
                };
            }

            return {
                ...prevState,
                isVisibleModal: false
            };
        });
    }, [propCheckedKeys, propMetrics]);

    const onCancel = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            isVisibleModal: false,
            checkedKeys: initialCheckedKeysRef.current,
            expandedKeys: initialCheckedKeysRef.current,
            metrics: initialMetricsRef.current
        }));
    }, []);

    const onToggleModalTagSettings = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            isVisibleModalTagSettings: !prevState.isVisibleModalTagSettings
        }));
    }, []);

    const onAction = useCallback(() => {
        if (propOnAction) {
            propOnAction(state.metrics);
        }
    }, [propOnAction, state.metrics]);

    const onCheck = useCallback((checkedKeys) => {
        setState(prevState => ({
            ...prevState,
            checkedKeys,
            metrics: reportUtils.getStrMetric(checkedKeys)
        }));
    }, []);

    const onExpand = useCallback((expandedKeys) => {
        setState(prevState => ({
            ...prevState,
            expandedKeys
        }));
    }, []);

    const onClearExpand = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            expandedKeys: []
        }));
    }, []);

    const onConfirm = useCallback(() => {
        onAction();
        onToggleModal();
        setServerStorage();
    }, [onAction, onToggleModal]);

    const onConfirmTagSettings = useCallback(() => {
        onAction();
        onToggleModalTagSettings();
        setServerStorage();
    }, [onAction, onToggleModalTagSettings]);

    const setServerStorage = useCallback(() => {
        if (propSaveServerStorage) {
            propSaveServerStorage(state.metrics);
        }
    }, [propSaveServerStorage, state.metrics]);

    const renderChildren = useCallback((level = 0, parentKey) => {
        return (
            (state.data[level] || [])
                .filter((item) => !parentKey || item.config.parentKey === parentKey)
                .map((item) => {
                        let config = item.config;
                        let children = config.metrics || config.children;
                        let nextLevel = config.metrics ? state.data.length - 1 : level + 1;

                        return (
                            <TreeNode
                                key={config.key}
                                title={(
                                    <span>
                                    {item.config.key === 'GroupBy' ? 'GroupBy' : item.title}
                                        {item.description
                                            ? <CustomTooltip id={config.key} placement={'right'} tooltip={item.description}><Icon name={'fa-question-circle rc-tree-help'} /></CustomTooltip>
                                            : null
                                        }
                                </span>
                                )}
                                disableCheckbox={item.config.key === 'GroupBy'}
                            >
                                {children
                                    ? renderChildren(nextLevel, config.key)
                                    : null
                                }
                            </TreeNode>
                        )
                    }
                )
        );
    }, [state.data]);

    const renderFilters = useCallback(() => {
        return (
            (state.data[0] || [])
                .map((filter,i) => {
                    return (
                        <TreeNode
                            key={filter.config.key}
                            title={(
                                <span>
                                {filter.config.key === 'GroupBy' ? 'GroupBy' : filter.title}
                                    {filter.description
                                        ? <CustomTooltip id={`column-${i}`} placement={'right'}
                                                         tooltip={filter.description}>
                                            <Icon name={'fa-question-circle rc-tree-help'}/>
                                        </CustomTooltip>
                                        : null
                                    }
                            </span>
                            )}
                        >
                            {state.data[1]
                                ? (state.data[1] || [])
                                    .filter(item => item.config.parentKey === filter.config.key)
                                    .map((option, j) => {
                                        return (
                                            <TreeNode
                                                key={option.config.key}
                                                title={(
                                                    <span>
                                                        {option.title}
                                                        {option.description
                                                            ? <CustomTooltip
                                                                id={`column-${i}-${j}`}
                                                                placement={'right'}
                                                                tooltip={option.description}
                                                            >
                                                                <Icon name={'fa-question-circle rc-tree-help'}/>
                                                            </CustomTooltip>
                                                            : null
                                                        }
                                                    </span>
                                                )}
                                            />
                                        );
                                    })
                                : null}
                        </TreeNode>
                    )
                })
        );
    }, [state.data]);

    const staticNames = t?.reportPage || {};

    return (
        <React.Fragment>
            <React.Fragment>
                <CustomTooltip id={`metricsPopupTooltip`} tooltip={staticNames.metricsTitle}>
                    <button
                        id="metricsPopup"
                        className="btn"
                        onClick={onToggleModal}
                    >
                        <img src={assets.icons.settings} alt=""/>
                    </button>
                </CustomTooltip>
                <CustomModal
                    className={'settingsMenu'}
                    title={staticNames.metricsTitle}
                    confirmName={staticNames.applyMetrics}
                    visible={state.isVisibleModal}
                    onClose={onCancel}
                    onConfirm={onConfirm}
                >
                    <div className="row">
                        <div className="col-7">
                            <Tree
                                className="myCls"
                                showLine
                                checkable
                                selectable={false}
                                showIcon={false}
                                defaultExpandedKeys={state.checkedKeys}
                                expandedKeys={state.expandedKeys}
                                checkedKeys={state.checkedKeys}
                                onExpand={onExpand}
                                onCheck={onCheck}
                            >
                                {renderChildren()}
                            </Tree>
                        </div>
                        <div className="col-5 d-flex text-right justify-content-center align-items-start">
                            <button
                                className="btn btn-outline btn-success"
                                type="button"
                                disabled={!state.expandedKeys.length}
                                onClick={onClearExpand}
                            >
                                {staticNames.collapseAll || staticNames.clearExpand}
                            </button>
                        </div>
                    </div>
                </CustomModal>
            </React.Fragment>
        </React.Fragment>
    );
};

StatisticsMetrics.propTypes = {
    data: PropTypes.array,
    checkedKeys: PropTypes.array,
    metrics: PropTypes.string,
    onAction: PropTypes.func,
    saveServerStorage: PropTypes.func,
    t: PropTypes.object
};

StatisticsMetrics.defaultProps = {
    data: [],
    checkedKeys: [],
    metrics: '',
    onAction: () => {},
    saveServerStorage: () => {},
    t: {}
};

export default withTranslation(StatisticsMetrics);
